import React from "react";
import styles from "./AssistantPageHeader.module.css"
import {Button, ButtonState} from "../../../../../../elements/button/Button";
import {useAppDispatch} from "../../../../../../../app/hooks";
import {FormName, openForm} from "../../../../../../elements/formProvider/formSlice";
import {openPopupModal, PopupModalName} from "../../../../../../elements/popupModalProvider/popupModalSlice";
import {BACKEND_URL, PROFILE, WIDGET_BASE_URL} from "../../../../../../../configs";
import {TabChooseButton} from "../../../../../../elements/tabChooseButton/TabChooseButton";
import {reimportVehicles} from "../../../../botSlice";
import {FullBotUi, VehicleDataProvider} from "../../../../../../../api/generated";
import {
    showCopiedSuccessfullyToastNotification,
    showErrorToastNotification,
    showSuccessToastNotification
} from "../../../../../../elements/toastNotification/toastNotificationSlice";
import {getAssistantVehicleDataProvider} from "../../../../../../../utils/assistantVehicleDataProvider";
import {WarningBadge} from "../../../../../../elements/warningBadge/WarningBadge";
import {ActionMenu, ActionMenuItem} from "../../../../../../elements/actionMenu/ActionMenu";
import {exportAssistant} from "../../../../transfer/assistantTransferSlice";

interface AssistantPageHeaderProps {
    bot: FullBotUi,
    /** Called after an import, so the page can reload everything it shows. */
    onConfigurationImported?: () => void,
    tabs: {
        [key: string]: {
            title: string
            onClick: () => void
            to?: string
            isActive: boolean
            iconSrc: string
        }
    }
}

export const AssistantPageHeader: React.FC<AssistantPageHeaderProps> = ({
                                                                            bot,
                                                                            tabs,
                                                                            onConfigurationImported
                                                                        }) => {
    const dispatch = useAppDispatch()
    const {publicId} = bot
    const assistantVehicleDataProvider = getAssistantVehicleDataProvider(bot.locationList)
    const canReimportVehicles = assistantVehicleDataProvider != null
        && assistantVehicleDataProvider !== VehicleDataProvider.KeyloopPipelineSearch

    const configurationIssues = bot.configurationIssues ?? []
    const hasConfigurationIssues = configurationIssues.length > 0
    const warnings = bot.warnings ?? []
    const hasWarnings = warnings.length > 0

    const handleCopyScriptClick = () => {
        navigator.clipboard.writeText(`<script type="text/javascript" id="chatbot-initials-script" data-bot-id="${publicId}" data-profile="${PROFILE}" data-platform-base-url="${BACKEND_URL}" src="${WIDGET_BASE_URL}/script-chatbot.js"></script>`);
        dispatch(
            showCopiedSuccessfullyToastNotification({
                fieldName: "Script",
            })
        );
    }

    const handleTestWidgetClick = () => {
        window.open(`/widget-demo?botId=${publicId}`)
    }

    const handleReimportVehiclesClick = async () => {
        try {
            await dispatch(reimportVehicles(publicId)).unwrap();
        } catch (error) {
            dispatch(showErrorToastNotification({
                message: typeof error === "string" && error ? error : "Failed to reimport vehicles"
            }));
        }
    }

    const handleEditClick = () => {
        dispatch(openForm({name: FormName.UPDATE_BOT, formProps: {publicId}}))
    }

    const handleExportClick = async () => {
        try {
            const fileName = await dispatch(exportAssistant(publicId)).unwrap();
            dispatch(showSuccessToastNotification({message: `Configuration exported as ${fileName}`}));
        } catch (error) {
            dispatch(showErrorToastNotification({
                message: typeof error === "string" && error ? error : "Failed to export the assistant configuration"
            }));
        }
    }

    const handleImportClick = () => {
        dispatch(openPopupModal({
            name: PopupModalName.IMPORT_ASSISTANT,
            popupModalProps: {
                publicId,
                assistantName: bot.siteName,
                onImported: onConfigurationImported
            }
        }))
    }

    const handleCloneClick = () => {
        dispatch(openPopupModal({
            name: PopupModalName.CLONE_ASSISTANT,
            popupModalProps: {
                publicId,
                assistantName: bot.siteName
            }
        }))
    }

    const handleDeleteClick = () => {
        dispatch(openPopupModal({
            name: PopupModalName.DELETE_BOT,
            popupModalProps: {
                publicId,
                entityName: bot.siteName,
                onClickRedirectPath: "/assistants"
            }
        }))
    }

    const handleOpenConfigurationIssuesClick = () => {
        dispatch(openPopupModal({
            name: PopupModalName.CONFIGURATION_ISSUES,
            popupModalProps: {
                title: "Configuration issues",
                tone: "error",
                issues: configurationIssues.map(issue => ({
                    message: issue.message || "Configuration issue detected.",
                    field: issue.field
                }))
            }
        }))
    }

    const menuItems: ActionMenuItem[] = [
        ...(canReimportVehicles ? [{
            label: "Reimport vehicles",
            iconSrc: "/reload.svg",
            disabled: !bot.isReimportFinished,
            onClick: handleReimportVehiclesClick
        }] : []),
        {
            label: "Export configuration",
            iconSrc: "/exportIcon.svg",
            onClick: handleExportClick
        },
        {
            label: "Import configuration",
            iconSrc: "/importIcon.svg",
            onClick: handleImportClick
        },
        {
            label: "Clone assistant",
            iconSrc: "/cloneIcon.svg",
            onClick: handleCloneClick
        },
        {
            label: "Delete assistant",
            iconSrc: "/deleteIcon.svg",
            danger: true,
            onClick: handleDeleteClick
        }
    ]

    return <div className={styles.wrapper}>
        <div className={styles.rowContainer}>
            <div className={styles.leftGroup}>
                {(hasConfigurationIssues || hasWarnings) && <div className={styles.badgeGroup}>
                    {hasConfigurationIssues && (
                        <button
                            type="button"
                            className={styles.configurationBadge}
                            onClick={handleOpenConfigurationIssuesClick}
                        >
                            Needs configuration: {configurationIssues.length} {configurationIssues.length === 1 ? "issue" : "issues"}
                        </button>
                    )}
                    <WarningBadge warnings={warnings}/>
                </div>}
                <span className={styles.name}>Assistant #{publicId.substring(publicId.length - 4)}</span>
            </div>
            <div className={styles.buttons}>
                <Button
                    title={"Copy script"}
                    secondTitle={"Copied"}
                    iconSrc={"/scriptIcon.svg"}
                    state={ButtonState.SECONDARY}
                    handleClick={handleCopyScriptClick}
                />
                <Button
                    title={"Test widget"}
                    iconSrc={"/playIcon.svg"}
                    state={ButtonState.SECONDARY}
                    handleClick={handleTestWidgetClick}
                />
                <Button
                    title={"Edit"}
                    iconSrc={"/editIcon.svg"}
                    state={ButtonState.SECONDARY}
                    handleClick={handleEditClick}
                />
                <ActionMenu items={menuItems}/>
            </div>
        </div>
        <div className={styles.tabContainer}>
            {Object.entries(tabs).map(([key, tab]) => (
                <TabChooseButton
                    key={key}
                    onClick={tab.onClick}
                    to={tab.to}
                    replace={true}
                    imageSrc={tab.iconSrc}
                    text={tab.title}
                    isActive={tab.isActive}
                />
            ))}
        </div>
    </div>
}
